// frontend/src/components/players/AudioPlayer.js
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { usePlayerContext } from '../../context/PlayerContext';
import { convertToPersianNumbers } from '../../utils/PersianNumberUtils';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, Gauge } from 'lucide-react';

const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds) || seconds <= 0) return convertToPersianNumbers('0:00');
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    const formattedSeconds = remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds;
    return convertToPersianNumbers(`${minutes}:${formattedSeconds}`);
};

const formatDuration = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds) || seconds <= 0) return convertToPersianNumbers('--:--');
    return formatTime(seconds);
};

const AudioPlayer = () => {
    const {
        currentMedia,
        registerAudioRef,
        togglePlayPause,
        isPlaying,
        closePlayer,
    } = usePlayerContext();

    const localAudioRef = useRef(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [isVolumeOpen, setIsVolumeOpen] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [canSeek, setCanSeek] = useState(false);

    const increaseSpeed = useCallback(() => {
        if (playbackRate < 2.5) {
            const newRate = Math.min(playbackRate + 0.25, 2.5);
            setPlaybackRate(newRate);
            if (localAudioRef.current) localAudioRef.current.playbackRate = newRate;
        }
    }, [playbackRate]);

    const decreaseSpeed = useCallback(() => {
        if (playbackRate > 0.5) {
            const newRate = Math.max(playbackRate - 0.25, 0.5);
            setPlaybackRate(newRate);
            if (localAudioRef.current) localAudioRef.current.playbackRate = newRate;
        }
    }, [playbackRate]);

    const resetSpeed = useCallback(() => {
        setPlaybackRate(1);
        if (localAudioRef.current) localAudioRef.current.playbackRate = 1;
    }, []);

    const handleSeek = useCallback((newTime) => {
        if (!localAudioRef.current) return;
        if (canSeek && !isNaN(duration)) {
            localAudioRef.current.currentTime = Math.max(0, Math.min(newTime, duration));
            setCurrentTime(localAudioRef.current.currentTime);
        } else {
            const onCanPlay = () => {
                if (localAudioRef.current) {
                    localAudioRef.current.currentTime = Math.max(0, Math.min(newTime, duration));
                    localAudioRef.current.removeEventListener('canplay', onCanPlay);
                }
            };
            localAudioRef.current.addEventListener('canplay', onCanPlay);
        }
    }, [canSeek, duration]);

    const handleSkipBack30 = useCallback(() => {
        const newTime = currentTime - 30;
        handleSeek(newTime);
    }, [currentTime, handleSeek]);

    const handleSkipForward30 = useCallback(() => {
        const newTime = currentTime + 30;
        handleSeek(newTime);
    }, [currentTime, handleSeek]);

    const handleProgressChange = useCallback((e) => {
        const newTime = parseFloat(e.target.value);
        handleSeek(newTime);
    }, [handleSeek]);

    const handleVolumeChange = useCallback((e) => {
        const newVolume = parseFloat(e.target.value) / 100;
        setVolume(newVolume);
        setMuted(newVolume === 0);
        if (localAudioRef.current) {
            localAudioRef.current.volume = newVolume;
            localAudioRef.current.muted = newVolume === 0;
        }
    }, []);

    const toggleMute = useCallback(() => {
        if (muted || volume === 0) {
            const newVolume = (volume > 0) ? volume : 0.5;
            setVolume(newVolume);
            setMuted(false);
            if (localAudioRef.current) {
                localAudioRef.current.volume = newVolume;
                localAudioRef.current.muted = false;
            }
        } else {
            setMuted(true);
            if (localAudioRef.current) localAudioRef.current.muted = true;
        }
    }, [muted, volume]);

    const handleTimeUpdate = useCallback(() => {
        if (localAudioRef.current) setCurrentTime(localAudioRef.current.currentTime);
    }, []);

    const handleLoadedMetadata = useCallback(() => {
        if (localAudioRef.current && !isNaN(localAudioRef.current.duration)) {
            setDuration(localAudioRef.current.duration);
            setVolume(localAudioRef.current.volume);
            setMuted(localAudioRef.current.muted);
            setCanSeek(true);
        }
    }, []);

    const handleCanPlay = useCallback(() => {
        setCanSeek(true);
    }, []);

    useEffect(() => {
        const audio = localAudioRef.current;
        if (audio) {
            registerAudioRef(audio);
            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
            audio.addEventListener('canplay', handleCanPlay);
            audio.addEventListener('timeupdate', handleTimeUpdate);
            audio.volume = volume;
            audio.muted = muted;
            audio.playbackRate = playbackRate;
            return () => {
                audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
                audio.removeEventListener('canplay', handleCanPlay);
                audio.removeEventListener('timeupdate', handleTimeUpdate);
            };
        }
    }, [registerAudioRef, handleLoadedMetadata, handleCanPlay, handleTimeUpdate, volume, muted, playbackRate]);

    if (!currentMedia) return null;
    const progressMax = (!isNaN(duration) && isFinite(duration)) ? duration : 0;

    return (
        <div 
            className="relative z-10 w-full max-w-[450px] mx-auto bg-white border-b border-gray-200 shadow-md rounded-lg"
            style={{ direction: 'ltr', textAlign: 'left' }}
        >
            <audio
                ref={localAudioRef}
                src={currentMedia.url}
                preload="auto"
                className="hidden"
            />

            {/* نوار پیشرفت */}
            <div className="px-3 pt-2">
                <input
                    type="range"
                    min="0"
                    max={progressMax}
                    value={currentTime}
                    onChange={handleProgressChange}
                    className="w-full h-1 bg-indigo-200 rounded-lg appearance-none cursor-pointer"
                    disabled={!canSeek || progressMax === 0}
                />
            </div>

            {/* بخش کنترل‌ها */}
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 px-3 py-2">
                {/* ستون چپ: عنوان و هنرمند */}
                <div className="min-w-0 truncate text-left">
                    <p className="text-[11px] font-bold text-indigo-800 truncate">
                        {currentMedia.title}
                    </p>
                    <span className="text-xs text-gray-600 truncate block">
                        {currentMedia.artist}
                    </span>
                </div>

                {/* ستون وسط: دکمه‌های پخش (بالا) و تنظیم سرعت (پایین) */}
                <div className="flex flex-col items-center gap-1">
                    {/* دکمه‌های پخش */}
                    <div className="flex items-center gap-2">
                        <button onClick={handleSkipBack30} className="p-1 rounded-full text-gray-600 hover:text-indigo-600">
                            <SkipBack size={18} />
                        </button>
                        <button onClick={togglePlayPause} className="p-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700">
                            {isPlaying ? <Pause size={18} fill="white" /> : <Play size={18} fill="white" />}
                        </button>
                        <button onClick={handleSkipForward30} className="p-1 rounded-full text-gray-600 hover:text-indigo-600">
                            <SkipForward size={18} />
                        </button>
                    </div>
                    {/* کنترل سرعت - بزرگ‌تر و خوانا */}
                    <div className="flex items-center gap-1.5 bg-purple-100 rounded-full px-3 py-1">
                        <button onClick={decreaseSpeed} className="text-sm font-bold text-purple-700 hover:text-indigo-600 w-6">
                            -
                        </button>
                        <span className="text-normal font-semibold text-purple-800 min-w-[60px] text-center">
                            {convertToPersianNumbers(playbackRate.toFixed(2))}x
                        </span>
                        <button onClick={increaseSpeed} className="text-sm font-bold text-purple-700 hover:text-indigo-600 w-6">
                            +
                        </button>
                        <button onClick={resetSpeed} className="text-xs text-purple-500 hover:text-indigo-600" title="بازنشانی به 1x">
                            <Gauge size={16} />
                        </button>
                    </div>
                </div>

                {/* ستون راست: زمان، صدا، بستن */}
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 min-w-[65px] text-right">
                        {formatTime(currentTime)} / {formatDuration(duration)}
                    </span>

                    {/* کنترل صدا */}
                    <div className="relative flex items-center">
                        <button onClick={() => setIsVolumeOpen(prev => !prev)} className="p-1 text-gray-600 hover:text-indigo-600">
                            {muted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                        </button>
                        {isVolumeOpen && (
                            <div className="absolute bottom-full right-0 mb-2 p-2 bg-white shadow-lg rounded-lg border border-gray-200 z-20">
                                <div className="h-24 flex justify-center items-center">
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        step="1"
                                        value={muted ? 0 : volume * 100}
                                        onChange={handleVolumeChange}
                                        className="h-20 w-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                        style={{ writingMode: 'bt-lr', WebkitAppearance: 'slider-vertical' }}
                                    />
                                </div>
                                <button onClick={toggleMute} className="w-full text-xs text-center text-gray-600 hover:text-indigo-600 pt-1">
                                    {muted || volume === 0 ? "بی‌صدا" : "صدا"}
                                </button>
                            </div>
                        )}
                    </div>

                    <button onClick={closePlayer} className="p-1 rounded-full text-gray-500 hover:text-red-600">
                        <X size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AudioPlayer;